/** Deal Locator types.
 *
 *  These mirror `backend/app/api/v1/deal_search.py` exactly. Row payloads keep
 *  the API's snake_case keys rather than being camel-cased on the way in — the
 *  column registry, the picker and the Excel export all key off the same
 *  strings, and translating them here would mean maintaining a second mapping
 *  that can silently drift from the server's.
 */

import { money, ratio } from './format'

export type ColumnKind =
  | 'text'
  | 'currency'
  | 'percent'
  | 'ratio'
  | 'number'
  | 'date'
  | 'stage'
  | 'status'

export type ColumnSpec = {
  key: string
  label: string
  kind: ColumnKind
  group: string
}

export type DealSearchSchema = {
  columns: ColumnSpec[]
  defaultColumns: string[]
  sortFields: string[]
  groupDimensions: Array<{ key: string; label: string }>
  stages: string[]
  datePresets: Array<{ key: string; label: string; field: string }>
  dateFields: Array<{ key: string; label: string }>
  statuses: Array<{ key: string; label: string }>
  maxPageSize: number
  exportRowCap: number
}

/** `label` is set where the stored value is a code (the kill reason). */
export type FacetValue = { value: string; count: number; label?: string }

export type Facets = {
  facets: Record<string, { recorded: boolean; values: FacetValue[] }>
  corpus: {
    totalDeals: number
    deadDeals: number
    liveDeals: number
    earliestReceived: string | null
    latestReceived: string | null
    earliestClosed: string | null
    latestClosed: string | null
  }
}

export type DealStatusFilter = 'active' | 'dead' | 'all'

export type SearchFilters = {
  q: string
  stages: string[]
  stageFrom: string | null
  stageTo: string | null
  assetTypes: string[]
  assignees: string[]
  originators: string[]
  investors: string[]
  capitalPrograms: string[]
  sponsors: string[]
  brokers: string[]
  locations: string[]
  priorities: string[]
  killReasons: string[]
  status: DealStatusFilter
  datePreset: string | null
  dateField: string
  dateFrom: string | null
  dateTo: string | null
  amountMin: number | null
  amountMax: number | null
  ltvMin: number | null
  ltvMax: number | null
  dscrMin: number | null
  dscrMax: number | null
}

export const EMPTY_FILTERS: SearchFilters = {
  q: '',
  stages: [],
  stageFrom: null,
  stageTo: null,
  assetTypes: [],
  assignees: [],
  originators: [],
  investors: [],
  capitalPrograms: [],
  sponsors: [],
  brokers: [],
  locations: [],
  priorities: [],
  killReasons: [],
  status: 'active',
  datePreset: null,
  dateField: 'created_at',
  dateFrom: null,
  dateTo: null,
  amountMin: null,
  amountMax: null,
  ltvMin: null,
  ltvMax: null,
  dscrMin: null,
  dscrMax: null,
}

/** Multi-select facet filters, in the order the rail renders them. */
export const FACET_FILTERS: Array<{
  facet: string
  filterKey: keyof SearchFilters
  label: string
}> = [
  { facet: 'asset_type', filterKey: 'assetTypes', label: 'Deal type' },
  // The API always filtered on priority; the rail never offered it (R5).
  { facet: 'priority', filterKey: 'priorities', label: 'Priority' },
  { facet: 'investor', filterKey: 'investors', label: 'Investor' },
  { facet: 'capital_program', filterKey: 'capitalPrograms', label: 'Capital program' },
  { facet: 'originator', filterKey: 'originators', label: 'Originator' },
  // The deal's owner: the catalogue's Responsible Party. It read 'Analyst',
  // which is a separate catalogue field (locator-02).
  { facet: 'assignee', filterKey: 'assignees', label: 'Responsible Party' },
  { facet: 'sponsor', filterKey: 'sponsors', label: 'Sponsor' },
  { facet: 'broker_name', filterKey: 'brokers', label: 'Broker' },
  { facet: 'location', filterKey: 'locations', label: 'Location' },
  // Why dead deals died — the kill picklist (randy-2.7).
  { facet: 'kill_reason_code', filterKey: 'killReasons', label: 'Kill reason' },
]

export type DealRow = {
  id: string
  property_name: string | null
  asset_type: string | null
  location: string | null
  borrower: string | null
  sponsor: string | null
  stage: string | null
  stage_index: number | null
  deal_status: string
  phase: string | null
  priority: string | null
  days_open: number | null
  loan_amount: number | null
  funded_amount: number | null
  rate: string | null
  maturity_date: string | null
  ltv: number | null
  dscr: number | null
  sponsor_credit: number | null
  assignee: string | null
  /** The catalogue's Analyst and Underwriter (custom fields), not the assignee. */
  analyst?: string | null
  underwriter?: string | null
  originator: string | null
  investor: string | null
  capital_program: string | null
  broker_name: string | null
  created_at: string | null
  originated: string | null
  funded_at: string | null
  dead_at: string | null
  dead_reason: string | null
  // The kill record (POST /loans/{id}/kill). Null on live deals, and the code
  // is null on a kill recorded as free text before the picklist.
  kill_reason_code?: string | null
  kill_reason_label?: string | null
  kill_reason_note?: string | null
  killed_at_stage?: string | null
  killed_by?: string | null
  kill_broker_contacted?: boolean | null
  kill_contact_method?: string | null
  kill_contact_date?: string | null
  source_ingestion_id: string | null
  /** A fraction (0.187 for 18.7%), like every 'percent' column: catalogue
   *  fields holding a value, out of the catalogue. */
  completeness: number
  /** The basis of `completeness`: populated of total catalogue fields. */
  completeness_populated?: number
  completeness_total?: number
  missing_fields: number
  missing_field_names: string[]
}

export type Totals = {
  dealCount: number
  totalVolume: number
  avgLtv: number | null
  avgDscr: number | null
  closedVolume: number
  closedCount: number
  deadCount: number
  avgDealSize: number | null
}

export type Bucket = {
  value: string | null
  label: string
  recorded: boolean
  isOverflow?: boolean
  dealCount: number
  totalVolume: number
  avgLtv: number | null
  avgDscr: number | null
}

export type GroupSummary = {
  dimension: string
  label: string
  buckets: Bucket[]
  bucketsOmitted: number
}

export type QueryResult = {
  rows: DealRow[]
  total: number
  page: number
  pageSize: number
  pageCount: number
  columns: string[]
  sortField: string
  sortDir: 'asc' | 'desc'
  appliedFilters: Record<string, unknown>
  totals: Totals
  groups?: GroupSummary
  elapsedMs: number
  savedSearch?: SavedSearch
}

export type PeriodResult = {
  label: string
  window: { field: string; from: string | null; to: string | null; preset: string | null } | null
  totals: Totals
  groups?: GroupSummary
  delta?: Record<string, { absolute: number | null; percent: number | null }>
}

export type CompareResult = {
  dateField: string
  baseline: string
  periods: PeriodResult[]
}

export type SavedSearch = {
  id: string
  name: string
  description: string
  ownerEmail: string
  filters: Partial<SearchFilters>
  columns: string[]
  sortField: string
  sortDir: 'asc' | 'desc'
  isShared: boolean
  createdAt: string | null
  updatedAt: string | null
  lastRunAt: string | null
}

/** The server's reading of an LTV figure (deal_search `_ltv_bound`, the same
 *  line PUT /loans draws): above 1.5 it is a whole-number percent, at or
 *  below it the fraction the column holds. */
const LTV_WHOLE_PERCENT_ABOVE = 1.5

/** An LTV filter bound as the percent it names, on the server's reading. A
 *  view saved before the rail sent fractions holds the typed 60, which the
 *  server reads as 60% — shown ×100 it read "6000". */
export function ltvBoundPercent(value: number): number {
  return value > LTV_WHOLE_PERCENT_ABOVE ? value : value * 100
}

/** A percent typed into the rail as the bound the API reads. Up to 100 it
 *  goes as the fraction the column holds; above 100 it goes as the percent
 *  typed, so the server refuses it naming that figure. Sent as a fraction,
 *  160 would be 1.6 — which the server reads as 1.6% and matches no deal in
 *  silence. The fraction is rounded to 10 places: 62.05 / 100 is
 *  0.6204999999999999, and a maximum sent as that leaves out a deal stored
 *  at exactly 0.6205. */
export function ltvBoundFromTyped(typed: number): number {
  return typed > 100 ? typed : Number((typed / 100).toFixed(10))
}

/** Which filter keys are currently doing something — drives the "clear" chips
 *  and the active-filter count, so nothing narrows a search invisibly. */
export function activeFilterChips(
  filters: SearchFilters,
  presetLabel: (key: string) => string,
): Array<{ key: keyof SearchFilters; label: string; clear: Partial<SearchFilters> }> {
  const chips: Array<{ key: keyof SearchFilters; label: string; clear: Partial<SearchFilters> }> = []

  if (filters.q.trim()) {
    chips.push({ key: 'q', label: `"${filters.q.trim()}"`, clear: { q: '' } })
  }
  if (filters.status !== 'active') {
    chips.push({
      key: 'status',
      label: filters.status === 'dead' ? 'Dead deals only' : 'Including dead deals',
      clear: { status: 'active' },
    })
  }
  if (filters.datePreset) {
    chips.push({
      key: 'datePreset',
      label: presetLabel(filters.datePreset),
      clear: { datePreset: null },
    })
  } else if (filters.dateFrom || filters.dateTo) {
    chips.push({
      key: 'dateFrom',
      label: `${filters.dateField === 'funded_at' ? 'Closed' : filters.dateField === 'dead_at' ? 'Marked dead' : 'Entered'} ${filters.dateFrom ?? 'inception'} → ${filters.dateTo ?? 'today'}`,
      clear: { dateFrom: null, dateTo: null },
    })
  }
  if (filters.stageFrom || filters.stageTo) {
    chips.push({
      key: 'stageFrom',
      label: `Stage ${filters.stageFrom ?? 'first'} → ${filters.stageTo ?? 'last'}`,
      clear: { stageFrom: null, stageTo: null },
    })
  }
  for (const { filterKey, label } of FACET_FILTERS) {
    const values = filters[filterKey] as string[]
    if (Array.isArray(values) && values.length) {
      chips.push({
        key: filterKey,
        label: `${label}: ${values.length === 1 ? values[0] : `${values.length} selected`}`,
        clear: { [filterKey]: [] } as Partial<SearchFilters>,
      })
    }
  }
  // The ranges narrowed the grid with no chip, so nothing said the result was
  // filtered and "Clear" — enabled by this count — stayed disabled (R5).
  const ranges: Array<{
    min: keyof SearchFilters
    max: keyof SearchFilters
    label: string
    show: (value: number) => string
  }> = [
    { min: 'amountMin', max: 'amountMax', label: 'Loan amount', show: (v) => money(v) },
    // The LTV is held as a fraction and typed as a percent.
    { min: 'ltvMin', max: 'ltvMax', label: 'LTV', show: (v) => `${Number(ltvBoundPercent(v).toFixed(1))}%` },
    { min: 'dscrMin', max: 'dscrMax', label: 'DSCR', show: (v) => `${ratio(v)}x` },
  ]
  for (const range of ranges) {
    const lo = filters[range.min] as number | null
    const hi = filters[range.max] as number | null
    if (lo === null && hi === null) continue
    chips.push({
      key: range.min,
      label:
        lo !== null && hi !== null
          ? `${range.label} ${range.show(lo)} – ${range.show(hi)}`
          : lo !== null
            ? `${range.label} ≥ ${range.show(lo)}`
            : `${range.label} ≤ ${range.show(hi as number)}`,
      clear: { [range.min]: null, [range.max]: null } as Partial<SearchFilters>,
    })
  }
  return chips
}
